import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { currentViewportRevision, onNativeViewportAcknowledged, refreshNativeViewport, sameNativeCallScope } from './nativeVideoViewport';
import { clearNativeMobileSession, hasPushRegistration, registerNativePush } from './mobileNotifications';
import { getNativeCapabilities, isNativeMobile, mobileDestination, nativeCall, nativeInvoke, nativeListen, type NativeCallState, type NativeListener } from './nativeBridge';
import { useHoldPullToRefresh } from './pullToRefreshHold';

// Capacitor reports the same launch URL after a page reload; session storage outlives the reload.
const CONSUMED_LAUNCH_URL_KEY = 'wisefriend.consumedLaunchUrl';
const launchUrlConsumed = (url: string) => {
  try {
    if (window.sessionStorage.getItem(CONSUMED_LAUNCH_URL_KEY) === url) return true;
    window.sessionStorage.setItem(CONSUMED_LAUNCH_URL_KEY, url);
  } catch { /* Storage can be unavailable; open the link as before. */ }
  return false;
};

/** Guest invitations open their own pre-join screen without an account. */
const guestMeetingLink = (url: URL) => /^\/meetings\/[^/]+\/?$/.test(url.pathname) && url.searchParams.has('guest');

/** App events live above routes so cold-start links and active calls survive web navigation. */
export function MobileRuntime() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const launchUrl = useRef<Promise<{ url?: string }> | null>(null);
  const location = useLocation();
  const locationRef = useRef(location); locationRef.current = location;
  const reconcileRestore = useRef<() => void>(() => undefined);
  const { user, isAuthLoading } = useAuth();
  const userId = user?.id;
  const current = useRef({ user, isAuthLoading });
  current.current = { user, isAuthLoading };
  const [destination, setDestination] = useState<string | null>(null);
  const [call, setCall] = useState<NativeCallState>({ state: 'idle' });
  const callRoute = useRef<{ meetingId: string; path: string } | null>(null);
  const authScope = useRef({ userId, isAuthLoading, generation: 0 });
  const callAuthGeneration = useRef(0);
  const initialAuthPending = useRef(isAuthLoading);
  const refreshNativeCall = useRef<() => void>(() => undefined);
  if (authScope.current.userId !== userId || authScope.current.isAuthLoading !== isAuthLoading) {
    authScope.current = { userId, isAuthLoading, generation: authScope.current.generation + 1 };
    callRoute.current = null;
  }
  const callBelongsToAuth = callAuthGeneration.current === authScope.current.generation && !isAuthLoading;
  // Retain the observed entry URL only for the live call. Guest entry needs its
  // existing invitation query when restoring after navigation; never invent it
  // from a native event or persist it outside this mounted runtime.
  if (!callBelongsToAuth || !['joining', 'joined'].includes(call.state)) callRoute.current = null;
  else if (call.meetingId && location.pathname === `/meetings/${encodeURIComponent(call.meetingId)}`) {
    callRoute.current = { meetingId: call.meetingId, path: location.pathname + location.search + location.hash };
  }
  const [offline, setOffline] = useState(() => !navigator.onLine);
  // A reload would drop the call's saved route and interrupt joining.
  useHoldPullToRefresh(['joining', 'joined'].includes(call.state));

  useLayoutEffect(() => { reconcileRestore.current(); }, [location.key, userId, isAuthLoading]);

  useEffect(() => {
    if (!isNativeMobile()) return;
    const listeners: NativeListener[] = [];
    let disposed = false;
    let callRevision = 0;
    let ownershipRevision = 0;
    let observedCall: NativeCallState = { state: 'idle' };
    const observeCall = (next: NativeCallState) => {
      const changed = !sameNativeCallScope(observedCall, next);
      observedCall = next;
      if (changed) {
        ownershipRevision += 1;
        reconcileRestore.current();
      }
    };
    const link = (value: unknown) => { const path = mobileDestination(value); if (path && !disposed) setDestination(path); };
    const add = async <T,>(plugin: string, name: string, handler: (event: T) => void) => {
      try {
        const listener = await nativeListen(plugin, name, handler);
        if (disposed) await listener.remove(); else listeners.push(listener);
      } catch { /* Older shells may not offer every optional plugin. */ }
    };
    const refreshCall = () => {
      const revision = ++callRevision;
      const authGeneration = authScope.current.generation;
      void nativeCall.getState().then((next) => {
        if (!disposed && revision === callRevision && authGeneration === authScope.current.generation) {
          observeCall(next);
          callAuthGeneration.current = authGeneration;
          setCall({ ...next });
        }
      }).catch(() => undefined);
    };
    refreshNativeCall.current = refreshCall;
    void add<{ url: string }>('App', 'appUrlOpen', (event) => link(event.url));
    void add<{ notification?: { data?: { path?: string } } }>('PushNotifications', 'pushNotificationActionPerformed', (event) => link(event.notification?.data?.path));
    void add<{ notification?: { extra?: { path?: string } } }>('LocalNotifications', 'localNotificationActionPerformed', (event) => link(event.notification?.extra?.path));
    void add<NativeCallState>('WiseFriendMobile', 'callStateChanged', (next) => {
      callRevision += 1;
      observeCall(next);
      if (!disposed) { callAuthGeneration.current = authScope.current.generation; setCall({ ...next }); }
    });
    type Restore = { meetingId: string; generation: number; restoreId: string; authGeneration: number; revision: number; afterViewport: number; ready: boolean; supported: boolean };
    let pendingRestore: Restore | undefined;
    const finishRestore = (request: Restore, restored: boolean) => {
      if (pendingRestore === request) pendingRestore = undefined;
      if (request.supported) void nativeCall.command(request, 'completePipRestore', { restoreId: request.restoreId, restored }).catch(() => undefined);
    };
    const restoreCurrent = (request: Restore) => !disposed && request === pendingRestore &&
      request.revision === ownershipRevision && request.authGeneration === authScope.current.generation && !authScope.current.isAuthLoading;
    reconcileRestore.current = () => {
      const request = pendingRestore;
      if (!request) return;
      if (!restoreCurrent(request)) { finishRestore(request, false); return; }
      if (!request.ready) return;
      if (locationRef.current.pathname !== `/meetings/${encodeURIComponent(request.meetingId)}`) { finishRestore(request, false); return; }
      refreshNativeViewport();
    };
    const stopViewport = onNativeViewportAcknowledged(receipt => {
      const request = pendingRestore;
      if (!request?.ready) return;
      if (!restoreCurrent(request)) { finishRestore(request, false); return; }
      if (receipt.meetingId !== request.meetingId || receipt.generation !== request.generation ||
          receipt.revision <= request.afterViewport || receipt.revision !== currentViewportRevision() ||
          locationRef.current.pathname !== `/meetings/${encodeURIComponent(request.meetingId)}` || document.visibilityState !== 'visible') return;
      finishRestore(request, true);
    });
    void add<{ type: string; meetingId?: string; generation?: number; restoreId?: string }>('WiseFriendMobile', 'callEvent', (event) => {
      if (event.type !== 'pip-restore' || !event.meetingId || !Number.isInteger(event.generation)) return;
      if (pendingRestore) finishRestore(pendingRestore, false);
      const revision = ownershipRevision;
      const authGeneration = authScope.current.generation;
      const request: Restore | undefined = typeof event.restoreId === 'string' && event.restoreId.length > 0
        ? { meetingId: event.meetingId, generation: event.generation!, restoreId: event.restoreId, authGeneration, revision, afterViewport: currentViewportRevision(), ready: false, supported: false } : undefined;
      pendingRestore = request;
      // Older v2 shells omit restoreId and retain their existing navigation path.
      void Promise.all([nativeCall.getState().catch(() => null), request ? getNativeCapabilities() : Promise.resolve(null)]).then(([next, capabilities]) => {
        if (request) request.supported = Boolean(capabilities?.pipRestoreAck);
        if (disposed || revision !== ownershipRevision || authGeneration !== authScope.current.generation ||
            authScope.current.isAuthLoading || callAuthGeneration.current !== authGeneration || !next || next.state !== 'joined' || next.accessVerifying || !next.meetingId ||
            next.meetingId !== event.meetingId || next.generation !== event.generation || (request && request !== pendingRestore)) {
          if (request) finishRestore(request, false);
          return;
        }
        if (request) {
          request.ready = true;
          // Never reuse an acknowledgement from before this ownership check.
          request.afterViewport = currentViewportRevision();
        }
        const target = callRoute.current && callRoute.current.meetingId === next.meetingId
          ? callRoute.current.path : `/meetings/${encodeURIComponent(next.meetingId)}`;
        const currentLocation = locationRef.current;
        if (target === currentLocation.pathname + currentLocation.search + currentLocation.hash) {
          // A redundant navigation can reload the meeting shell and discard its
          // breakout state while the native peer stays joined. Keep this route,
          // but still require a fresh viewport for an acknowledged restore.
          if (request) reconcileRestore.current();
          else refreshNativeViewport();
        } else navigateRef.current(target);
      }).catch(() => { if (request) finishRestore(request, false); });
    });
    void add<{ isActive: boolean }>('App', 'appStateChange', (event) => { if (event.isActive) refreshCall(); });
    // Capacitor retains this URL for the process lifetime. Read it once, and
    // keep native listeners stable while BrowserRouter replaces navigate.
    launchUrl.current ??= nativeInvoke<{ url?: string }>('App', 'getLaunchUrl');
    void launchUrl.current.then((event) => {
      if (!disposed && typeof event?.url === 'string' && !launchUrlConsumed(event.url)) link(event.url);
    }).catch(() => undefined);
    refreshCall();
    const visibility = () => { if (document.visibilityState !== 'visible' && pendingRestore) finishRestore(pendingRestore, false); };
    document.addEventListener('visibilitychange', visibility);
    const online = () => setOffline(!navigator.onLine);
    window.addEventListener('online', online); window.addEventListener('offline', online);
    return () => {
      disposed = true;
      if (pendingRestore) finishRestore(pendingRestore, false);
      reconcileRestore.current = () => undefined;
      stopViewport();
      document.removeEventListener('visibilitychange', visibility);
      listeners.forEach((listener) => { void listener.remove().catch(() => undefined); });
      window.removeEventListener('online', online); window.removeEventListener('offline', online);
    };
  }, []);

  useEffect(() => {
    // Initial auth bootstrap may finish after the first native snapshot. Rebind
    // that existing call once; later account changes must retire its UI scope.
    if (!isAuthLoading && initialAuthPending.current) {
      initialAuthPending.current = false;
      refreshNativeCall.current();
    }
  }, [isAuthLoading]);

  useEffect(() => {
    if (!destination || isAuthLoading) return;
    setDestination(null);
    const path = new URL(destination, window.location.origin);
    if (!user && !guestMeetingLink(path)) {
      navigate('/login', { state: { from: { pathname: path.pathname, search: path.search, hash: path.hash } } });
    } else navigate(destination);
  }, [destination, isAuthLoading, navigate, user]);

  useEffect(() => {
    if (!isNativeMobile() || isAuthLoading) return;
    // Guest calls can exist before sign-in. AuthContext explicitly leaves calls
    // when an authenticated session ends; native lifecycle checks revoke expired access.
    if (!userId) { clearNativeMobileSession({ leaveCall: false }); return; }
    if (!hasPushRegistration(userId)) return;
    let active = true;
    void getNativeCapabilities().then(async (capabilities) => {
      if (active && capabilities && current.current.user?.id === userId) await registerNativePush(capabilities, userId, { prompt: false });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [isAuthLoading, userId]);

  // Native lifecycle monitoring owns termination even when the web view is
  // suspended. A prior Daily actual_ended_at can belong to a rejoined session.

  if (!isNativeMobile()) return null;
  return <>
    {offline && <div role="status" className="fixed inset-x-0 top-0 z-[1000] bg-amber-100 px-4 py-2 text-center text-sm text-amber-900" style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}>You’re offline. Your connection is needed to save changes.</div>}
    {callBelongsToAuth && call.state === 'joined' && call.meetingId && !location.pathname.startsWith('/meetings/') && <button type="button" onClick={() => navigate(callRoute.current && callRoute.current.meetingId === call.meetingId ? callRoute.current.path : `/meetings/${encodeURIComponent(call.meetingId || '')}`)} className="fixed right-4 z-[450] rounded-full bg-[#1d4137] px-5 py-3 text-sm font-semibold text-white shadow-lg" style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }}>Return to your call</button>}
  </>;
}
